import { useEffect, type CSSProperties } from "react";
import { createPortal } from "react-dom";
import { Icon } from "@/components/ui/Icon";
import { t } from "@/lib/strings";

// In-app image viewer: a dimmed full-screen overlay (portaled to body) with the
// image centred. Click the backdrop or press Esc to close; the image itself and
// the controls don't close it. An "open original" link is the escape hatch for
// full-res / download. (Video support will hang off the same shell later.)
export function ImageLightbox({
  src,
  alt,
  onClose,
}: {
  src: string;
  alt?: string;
  onClose: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  return createPortal(
    <div role="dialog" aria-modal="true" onClick={onClose} style={backdrop}>
      <div style={controls} onClick={(e) => e.stopPropagation()}>
        <a
          href={src}
          target="_blank"
          rel="noreferrer"
          title={t.lightboxOpenOriginal}
          style={openLink}
        >
          {t.lightboxOpenOriginal}
        </a>
        <button type="button" onClick={onClose} title={t.closeEsc} style={closeBtn}>
          <Icon name="x" size={20} color="#fff" />
        </button>
      </div>
      <img src={src} alt={alt ?? ""} onClick={(e) => e.stopPropagation()} style={image} />
    </div>,
    document.body,
  );
}

const backdrop: CSSProperties = {
  position: "fixed",
  inset: 0,
  zIndex: 9999,
  background: "rgba(0,0,0,0.86)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 32,
  cursor: "zoom-out",
};

const controls: CSSProperties = {
  position: "absolute",
  top: 16,
  right: 18,
  display: "flex",
  alignItems: "center",
  gap: 14,
};

const openLink: CSSProperties = {
  color: "rgba(255,255,255,0.85)",
  fontSize: 13,
  fontWeight: 500,
  textDecoration: "none",
  fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
};

const closeBtn: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 34,
  height: 34,
  borderRadius: 999,
  border: "none",
  background: "rgba(255,255,255,0.12)",
  cursor: "pointer",
  padding: 0,
};

const image: CSSProperties = {
  maxWidth: "92vw",
  maxHeight: "88vh",
  objectFit: "contain",
  borderRadius: 8,
  cursor: "default",
  boxShadow: "0 10px 40px rgba(0,0,0,0.5)",
};
